import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../api/axios';
import router from '../router';
import { webAuthnService } from '../services/webauthn';

interface User {
    id: number;
    name: string;
    email: string;
    position?: {
        name: string;
        job?: {
            title: string;
            responsibilities: Array<{
                id: number;
                responsibility: string;
                is_primary: boolean;
            }>
        }
    };
    department?: { name: string };
    branch?: { name: string };
    is_volunteer?: boolean;
    employee_code?: string;
    photo_url?: string;
    employment_type?: string;
    join_date?: string;
    id_card_path?: string;
    web_authn_credentials_exists?: boolean;
    // Add other fields as needed
}

export const useAuthStore = defineStore('auth', () => {
    const token = ref<string | null>(localStorage.getItem('auth_token'));
    const user = ref<User | null>(null);
    const isAuthenticated = computed(() => !!token.value);
    const hasWebAuthn = computed(() => !!user.value?.web_authn_credentials_exists);

    async function login(login_id: string, password: string) {
        try {
            // 1. Get CSRF cookie first (Sanctum) - REMOVED for Token Auth
            // Note: csrf-cookie is usually at root, not under /api
            // const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
            // const rootURL = baseURL.replace(/\/api\/?$/, ''); // Remove trailing /api or /api/
            // await api.get(`${rootURL}/sanctum/csrf-cookie`);

            // 2. Login to get token
            const response = await api.post('/auth/token', {
                login_id,
                password,
                device_name: 'laz-employee-pwa'
            });

            token.value = response.data.token;
            localStorage.setItem('auth_token', token.value as string);

            // 3. Fetch user details
            await fetchUser();

            // 4. Init FCM
            initFcm();

            router.push({ name: 'home' });
        } catch (error) {
            console.error('Login failed', error);
            throw error;
        }
    }

    async function fetchUser() {
        if (!token.value) return;
        try {
            // Fetch detailed employee profile instead of just user
            // The endpoint is /api/employee/profile
            const response = await api.get('/employee/profile');
            // The API returns { user: {...}, employee: {...} }
            // We'll flatten it or store it as is. For simplicity let's store the combined object
            const userData = { ...response.data.user, ...response.data.employee };
            user.value = userData;

            // Save for quick login
            if (userData.email) {
                localStorage.setItem('last_login_email', userData.email);
            }
            if (userData.name) {
                localStorage.setItem('last_login_name', userData.name);
            }
            if (userData.web_authn_credentials_exists !== undefined) {
                localStorage.setItem('has_webauthn', String(userData.web_authn_credentials_exists));
            }
        } catch (error) {
            console.error('Fetch user failed', error);
            logout();
        }
    }

    function logout() {
        token.value = null;
        user.value = null;
        localStorage.removeItem('auth_token');
        router.push({ name: 'login' });
    }

    async function initFcm() {
        if (!isAuthenticated.value) return;

        try {
            // Dynamic import to avoid issues if firebase isn't fully set up or valid in all envs
            const { requestForToken } = await import('../firebase');
            const fcmToken = await requestForToken();

            if (fcmToken) {
                await api.post('/fcm-token', {
                    token: fcmToken,
                    device_type: 'web'
                });
                console.log('FCM Token synced successfully');
            }
        } catch (error: any) {
            console.error('FCM init failed', error);
            // DEBUG: Alert on mobile if init fails, remove before prod if annoying
            if (import.meta.env.DEV) {
                alert('FCM Init Failed: ' + (error?.message || error));
            }
        }
    }

    async function loginWebAuthn(email: string) {
        try {
            const optionsRes = await api.post('/webauthn/login/options', { email });
            const credential = await webAuthnService.login(optionsRes.data);
            const response = await api.post('/webauthn/login', {
                email,
                ...credential
            });

            token.value = response.data.token;
            localStorage.setItem('auth_token', token.value as string);

            await fetchUser();
            initFcm();

            router.push({ name: 'home' });

        } catch (error) {
            console.error('WebAuthn Login Failed', error);
            throw error;
        }
    }

    async function registerWebAuthn() {
        if (!isAuthenticated.value) return;
        try {
            const optionsRes = await api.post('/webauthn/register/options');
            const credential = await webAuthnService.register(optionsRes.data);
            await api.post('/webauthn/register', credential);
            // Update local user state immediately
            if (user.value) {
                user.value.web_authn_credentials_exists = true;
                localStorage.setItem('has_webauthn', 'true');
            }
            return true;
        } catch (error) {
            console.error('WebAuthn Registration Failed', error);
            throw error;
        }
    }

    return {
        token,
        user,
        isAuthenticated,
        login,
        fetchUser,
        logout,
        initFcm,
        loginWebAuthn,
        registerWebAuthn,
        hasWebAuthn
    };
});
